import { PickupReport } from '@/components/reports';
import { PICKUP_ANALYSIS_HEADER_SLOT_ID } from '@/components/reports/pickupAnalysisWorkspace';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { PICKUP_PIPELINE_HEADER_SLOT_ID, PickupPipeline } from './PickupPipeline';

export type PickupWorkspaceTab = 'analysis' | 'pipeline';

interface PickupWorkspaceProps {
  activeTab: PickupWorkspaceTab;
  onTabChange: (tab: PickupWorkspaceTab) => void;
  canRunPricingActions?: boolean;
  canManagePipelineActions?: boolean;
}

export function PickupWorkspace({
  activeTab,
  onTabChange,
  canRunPricingActions = true,
  canManagePipelineActions = true
}: PickupWorkspaceProps) {
  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => onTabChange(value as PickupWorkspaceTab)}
      className={cn(
        'bg-background',
        activeTab === 'pipeline'
          ? 'flex h-full min-h-0 flex-col overflow-hidden [contain:strict]'
          : 'min-h-full'
      )}
      data-pickup-workspace
    >
      <header className={cn(
        'z-20 grid min-h-[54px] shrink-0 gap-3 border-b bg-card/95 px-4 py-1.5 backdrop-blur-sm md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center md:px-5',
        activeTab === 'analysis' && 'sticky top-0'
      )}>
        <div className="flex min-w-0 items-baseline gap-2.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Secondary
          </span>
          <span className="text-border" aria-hidden="true">/</span>
          <h1 className="truncate font-serif text-2xl font-medium text-primary">Pick Up</h1>
        </div>

        <TabsList aria-label="Pick Up workspace" className="w-fit bg-muted/80">
          <TabsTrigger value="analysis">1 · Analysis</TabsTrigger>
          <TabsTrigger value="pipeline">2 · Pipeline</TabsTrigger>
        </TabsList>

        <div className="flex min-w-0 items-center justify-end">
          <div
            id={PICKUP_ANALYSIS_HEADER_SLOT_ID}
            className={activeTab === 'analysis' ? 'min-w-0' : 'hidden'}
          />
          <div
            id={PICKUP_PIPELINE_HEADER_SLOT_ID}
            className={activeTab === 'pipeline' ? 'min-w-0' : 'hidden'}
          />
        </div>
      </header>

      <TabsContent value="analysis" className="m-0">
        <PickupReport embedded canRunPricingActions={canRunPricingActions} />
      </TabsContent>
      <TabsContent value="pipeline" className="m-0 min-h-0 flex-1 overflow-hidden">
        <PickupPipeline embedded canManageActions={canManagePipelineActions} />
      </TabsContent>
    </Tabs>
  );
}

export default PickupWorkspace;
